package horcrux.common

import arrow.core.NonEmptyList
import com.benasher44.uuid.Uuid
import com.benasher44.uuid.uuidFrom
import horcrux.common.api.dtos.types.DocumentType
import horcrux.common.api.dtos.types.DocumentTypeName
import horcrux.common.extensions.toNonEmptyList
import horcrux.common.extensions.toNonEmptyMap
import horcrux.common.types.NonEmptyMap
import horcrux.common.utils.getOrThrow
import kotlinx.serialization.KSerializer
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.MapSerializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.listSerialDescriptor
import kotlinx.serialization.descriptors.mapSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

interface NonEmptyListSerializer<A> : KSerializer<NonEmptyList<A>> {
    val elementSerializer: KSerializer<A>
    override val descriptor: SerialDescriptor
        get() = listSerialDescriptor(elementSerializer.descriptor)

    override fun serialize(encoder: Encoder, value: NonEmptyList<A>) {
        encoder.encodeSerializableValue(ListSerializer(elementSerializer), value)
    }

    override fun deserialize(decoder: Decoder): NonEmptyList<A> =
        decoder.decodeSerializableValue(ListSerializer(elementSerializer)).toNonEmptyList().getOrThrow()
}

interface NonEmptyMapSerializer<A, B> : KSerializer<NonEmptyMap<A, B>> {
    val keySerializer: KSerializer<A>
    val valueSerializer: KSerializer<B>
    override val descriptor: SerialDescriptor
        get() = mapSerialDescriptor(keySerializer.descriptor, valueSerializer.descriptor)

    override fun serialize(encoder: Encoder, value: NonEmptyMap<A, B>) {
        encoder.encodeSerializableValue(MapSerializer(keySerializer, valueSerializer), value)
    }

    override fun deserialize(decoder: Decoder): NonEmptyMap<A, B> =
        decoder.decodeSerializableValue(MapSerializer(keySerializer, valueSerializer)).toNonEmptyMap().getOrThrow()
}

object UuidSerializer : KSerializer<Uuid> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("Uuid", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: Uuid) {
        encoder.encodeString(value.toString())
    }

    override fun deserialize(decoder: Decoder): Uuid {
        return uuidFrom(decoder.decodeString())
    }
}

object DocumentTypeSerializer : KSerializer<DocumentType> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("DocumentType", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: DocumentType) {
        encoder.encodeString(value.name)
    }

    override fun deserialize(decoder: Decoder): DocumentType {
        val documentTypeName = DocumentTypeName(decoder.decodeString())
        return DocumentType.invoke(documentTypeName).getOrThrow()
    }
}
